import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';

const BillingAdmin = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [selected, setSelected] = useState({});

  useEffect(() => {
    const loadSubscriptions = async () => {
      try {
        setLoading(true);
        setErrorMessage('');
        const res = await api.get('/billing/subscriptions');
        setSubscriptions(res.data || []);
      } catch (error) {
        console.error('Error al cargar suscripciones:', error);
        setErrorMessage('No se pudieron cargar las suscripciones.');
      } finally {
        setLoading(false);
      }
    };

    loadSubscriptions();
  }, []);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return subscriptions.filter((sub) => {
      const matchesQuery =
        !normalized ||
        sub.name?.toLowerCase().includes(normalized) ||
        sub.plan_id?.toLowerCase().includes(normalized);
      const matchesStatus =
        statusFilter === 'all' ? true : sub.subscription_status === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [subscriptions, query, statusFilter]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      const aValue = (a[sortKey] || '').toString().toLowerCase();
      const bValue = (b[sortKey] || '').toString().toLowerCase();
      if (aValue < bValue) return -1 * dir;
      if (aValue > bValue) return 1 * dir;
      return 0;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const allSelected = sorted.length > 0 && sorted.every((s) => selected[s.id]);
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected({});
      return;
    }
    const next = {};
    sorted.forEach((sub) => { next[sub.id] = true; });
    setSelected(next);
  };
  const toggleRow = (id) => setSelected((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleExport = () => {
    const rows = sorted.filter((s) => selected[s.id]);
    if (!rows.length) {
      return;
    }
    const header = ['name', 'plan_id', 'subscription_status'];
    const csv = [
      header.join(','),
      ...rows.map((s) => [s.name, s.plan_id, s.subscription_status].map((v) => `"${v || ''}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'suscripciones.csv';
    link.click();
  };

  return (
    <div data-testid="billing-admin-page">
      <h1 className="text-4xl font-heading font-bold mb-6">Administracion de Facturacion</h1>
      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Input
            placeholder="Buscar por empresa o plan"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="max-w-xs"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="all">Todos</option>
            <option value="active">Activo</option>
            <option value="inactive">Inactivo</option>
            <option value="past_due">Past due</option>
          </select>
          <div className="ml-auto">
            <Button variant="outline" onClick={handleExport} disabled={!Object.values(selected).some(Boolean)}>
              Exportar CSV
            </Button>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-0">
          {loading && (
            <div className="px-6 py-6 text-sm text-muted-foreground">Cargando suscripciones...</div>
          )}
          {!loading && errorMessage && (
            <div className="px-6 py-6 text-sm text-destructive">{errorMessage}</div>
          )}
          {!loading && !errorMessage && sorted.length === 0 && (
            <div className="px-6 py-6 text-sm text-muted-foreground">No hay suscripciones.</div>
          )}
          {!loading && !errorMessage && sorted.length > 0 && (
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">
                    <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                  </th>
                  <th className="px-6 py-3 text-left cursor-pointer" onClick={() => toggleSort('name')}>Empresa</th>
                  <th className="px-6 py-3 text-left cursor-pointer" onClick={() => toggleSort('plan_id')}>Plan</th>
                  <th className="px-6 py-3 text-left cursor-pointer" onClick={() => toggleSort('subscription_status')}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((sub) => (
                  <tr key={sub.id} className="border-t">
                    <td className="px-4 py-4">
                      <input type="checkbox" checked={Boolean(selected[sub.id])} onChange={() => toggleRow(sub.id)} />
                    </td>
                    <td className="px-6 py-4">{sub.name}</td>
                    <td className="px-6 py-4">{sub.plan_id || '-'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${sub.subscription_status === 'active' ? 'bg-emerald-500/15 text-emerald-700' : 'bg-gray-500/15'}`}>
                        {sub.subscription_status || 'inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
export default BillingAdmin;
